import { Checkbox, FormControlLabel, Grid, TextField } from '@mui/material';
import { NamedFeature } from 'interfaces/features';
import { default as React } from 'react';
import { BoxPlotParams, HistogramPlotParams, PieChartParams, ScatterPlotParams, ViolinPlotParams } from './types';
import { VirtualizedFeaturesMultiSelect, VirtualizedFeaturesSelect } from './VirtualizedFeaturesSelect';

interface FeaturesPlotProps {
  featuresData: NamedFeature[];
  isLoadingFeaturesData: boolean;
}

export const ScatterPlot: React.FC<
  React.PropsWithChildren<
    FeaturesPlotProps & {
      plot: ScatterPlotParams;
      onChange: (e: any, newPlot: ScatterPlotParams) => void;
    }
  >
> = ({ plot, featuresData, isLoadingFeaturesData, onChange }) => {
  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={4.5}>
        <VirtualizedFeaturesSelect
          featuresData={featuresData}
          isLoadingFeaturesData={isLoadingFeaturesData}
          value={plot.yAxis || ''}
          onChange={(e, featureKey) => {
            onChange(e, { ...plot, yAxis: featureKey || '' });
          }}
          label="Y axis"
        />
      </Grid>
      <Grid item xs={4.5}>
        <VirtualizedFeaturesSelect
          featuresData={featuresData}
          isLoadingFeaturesData={isLoadingFeaturesData}
          value={plot.xAxis || ''}
          onChange={(e, featureKey) => {
            onChange(e, { ...plot, xAxis: featureKey || '' });
          }}
          label="X axis"
        />
      </Grid>
      <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(plot.includeTrendline)}
              onChange={(e) => {
                onChange(e, { ...plot, includeTrendline: e.target.checked });
              }}
              size="small"
              onBlur={(e) => {
                e.stopPropagation();
              }}
            />
          }
          label="Trendline"
        />
      </Grid>
    </Grid>
  );
};

export const ViolinPlot: React.FC<
  React.PropsWithChildren<
    FeaturesPlotProps & {
      plot: ViolinPlotParams;
      onChange: (e: any, newPlot: ViolinPlotParams) => void;
    }
  >
> = ({ plot, featuresData, isLoadingFeaturesData, onChange }) => {
  return (
    <Grid item xs={11}>
      <VirtualizedFeaturesMultiSelect
        featuresData={featuresData}
        isLoadingFeaturesData={isLoadingFeaturesData}
        value={plot.columns || []}
        onChange={(e, featureKey) => {
          onChange(e, { ...plot, columns: featureKey });
        }}
      />
    </Grid>
  );
};

export const BoxPlot: React.FC<
  React.PropsWithChildren<
    FeaturesPlotProps & {
      plot: BoxPlotParams;
      onChange: (e: any, newPlot: BoxPlotParams) => void;
    }
  >
> = ({ plot, featuresData, isLoadingFeaturesData, onChange }) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={11}>
        <VirtualizedFeaturesMultiSelect
          featuresData={featuresData}
          isLoadingFeaturesData={isLoadingFeaturesData}
          value={plot.columns || []}
          onChange={(e, featureKey) => {
            onChange(e, { ...plot, columns: featureKey });
          }}
        />
      </Grid>
    </Grid>
  );
};

export const HistogramPlot: React.FC<
  React.PropsWithChildren<
    FeaturesPlotProps & {
      plot: HistogramPlotParams;
      onChange: (e: any, newPlot: HistogramPlotParams) => void;
    }
  >
> = ({ plot, featuresData, isLoadingFeaturesData, onChange }) => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={9}>
        <VirtualizedFeaturesSelect
          featuresData={featuresData}
          isLoadingFeaturesData={isLoadingFeaturesData}
          value={plot.column || ''}
          onChange={(e, featureKey) => {
            onChange(e, { ...plot, column: featureKey || '' });
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <TextField
          size="small"
          label="Bins"
          type="number"
          value={plot.bins || 0}
          onChange={(e) => {
            const newNumBins = parseInt(e.target.value, 10);
            if (!isNaN(newNumBins)) {
              onChange(e, { ...plot, bins: newNumBins });
            }
          }}
          inputProps={{ min: 1 }}
        />
      </Grid>
    </Grid>
  );
};

export const PieChartPlot: React.FC<
  React.PropsWithChildren<
    FeaturesPlotProps & {
      plot: PieChartParams;
      onChange: (e: any, newPlot: PieChartParams) => void;
    }
  >
> = ({ plot, featuresData, isLoadingFeaturesData, onChange }) => {
  return (
    <Grid item xs={11}>
      <VirtualizedFeaturesSelect
        featuresData={featuresData}
        isLoadingFeaturesData={isLoadingFeaturesData}
        value={plot.column || ''}
        onChange={(e, featureKey) => {
          onChange(e, { ...plot, column: featureKey || '' });
        }}
      />
    </Grid>
  );
};
